"use client"

import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { getCategoryRoutePath } from "@/lib/util/category-helpers"
import { ChevronDown } from "lucide-react"
import { useRef, useState } from "react"
import type { CategoryNavItem as CategoryWithStrapi } from "./index"

const HOVER_CLOSE_DELAY_MS = 200

/**
 * "Kategorije" trigger; opens on hover (same pattern as CategoryDropdown).
 * Used in the floating nav when maxVisible limits how many categories show as primary links.
 */
export function MoreCategoriesDropdown({
  categories,
  allCategories,
  variant = "default",
}: {
  categories: CategoryWithStrapi[]
  allCategories: CategoryWithStrapi[]
  variant?: "default" | "under-row"
}) {
  const [isOpen, setIsOpen] = useState(false)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  if (categories.length === 0) return null

  const clearCloseTimer = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const openMenu = () => {
    clearCloseTimer()
    setIsOpen(true)
  }

  const scheduleClose = () => {
    clearCloseTimer()
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), HOVER_CLOSE_DELAY_MS)
  }

  const medusaHref = (category: CategoryWithStrapi) =>
    `/categories/${getCategoryRoutePath(category, allCategories)}`

  const isUnderRow = variant === "under-row"

  return (
    <div
      className="relative z-50"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={`flex items-center gap-1 transition-all duration-200 outline-none ${
          isUnderRow
            ? isOpen
              ? "text-white font-bold"
              : "text-cream/90 hover:text-white font-normal text-sm px-1 py-0.5"
            : isOpen
              ? "text-gold bg-navy/10 rounded-lg px-4 py-2"
              : "text-navy hover:text-gold hover:bg-navy/5 rounded-lg px-4 py-2"
        }`}
      >
        <span className={isUnderRow ? undefined : "font-medium"}>Kategorije</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-full mt-2 w-56 rounded-xl border border-stone-200 bg-white py-2 shadow-lg z-50"
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
        >
          <ul className="py-1">
            {categories.map((category) => (
              <li key={category.id}>
                <LocalizedClientLink
                  href={medusaHref(category)}
                  className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-navy/5 hover:text-navy transition-colors"
                >
                  {category.name}
                </LocalizedClientLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
